import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Toaster } from 'react-hot-toast'
import HomePage from './pages/HomePage'
import AuthLoginPage from './pages/AuthLoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import PendingApprovalPage from './pages/PendingApprovalPage'
import PoolsPage from './pages/PoolsPage'
import MembersPage from './pages/MembersPage'
import MemberDetailPage from './pages/MemberDetailPage'
import EventsPage from './pages/EventsPage'
import EventDetailPage from './pages/EventDetailPage'
import LocationsPage from './pages/LocationsPage'
import AdminPage from './pages/AdminPage'
import ProfilePage from './pages/ProfilePage'
import BourbonSwapPage from './pages/BourbonSwapPage'
import FootballPoolsAdminPage from './pages/FootballPoolsAdminPage'
import CreateFootballPoolPage from './pages/CreateFootballPoolPage'
import PoolDetailPage from './pages/PoolDetailPage'
import HorseRacingPoolsAdminPage from './pages/HorseRacingPoolsAdminPage'
import CreateHorseRacingPoolPage from './pages/CreateHorseRacingPoolPage'
import HorseRacingPoolDetailPage from './pages/HorseRacingPoolDetailPage'
import HorseRacingPoolStandingsPage from './pages/HorseRacingPoolStandingsPage'
import HorseRacingPoolPicksPage from './pages/HorseRacingPoolPicksPage'
import ViewMyHorsePicksPage from './pages/ViewMyHorsePicksPage'
import HorseRacingPoolResultsPage from './pages/HorseRacingPoolResultsPage'
import PoolPicksPage from './pages/PoolPicksPage'
import ViewMyPicksPage from './pages/ViewMyPicksPage'
import ViewMyPicksRouter from './pages/ViewMyPicksRouter'
import PoolDetailRouter from './pages/PoolDetailRouter'
import EditHorseRacingPoolPage from './pages/EditHorseRacingPoolPage'
import PoolStandingsPage from './pages/PoolStandingsPage'
import PoolStandingsRouter from './pages/PoolStandingsRouter'
import PoolGameResultsPage from './pages/PoolGameResultsPage'
import EditPoolPage from './pages/EditPoolPage'
import LegacyPage from './pages/LegacyPage'
import HistoricRosterPage from './pages/legacy/HistoricRosterPage'
import KazyCupPage from './pages/legacy/KazyCupPage'
import MemorialPage from './pages/legacy/MemorialPage'
import GalleryPage from './pages/legacy/GalleryPage'
import RailbirdGamesPage from './pages/legacy/RailbirdGamesPage'
import GameDetailPage from './pages/legacy/GameDetailPage'
import EditGamePage from './pages/legacy/EditGamePage'
import CreateGamePage from './pages/legacy/CreateGamePage'
import RailbirdLedgerPage from './pages/legacy/RailbirdLedgerPage'
import GolfOutingsPage from './pages/GolfOutingsPage'
import GolfCoursesPage from './pages/GolfCoursesPage'
import GolfOutingDetailPage from './pages/GolfOutingDetailPage'
import GolfScorecardPage from './pages/GolfScorecardPage'
import GolfLeaderboardPage from './pages/GolfLeaderboardPage'
import ChatPage from './pages/ChatPage'
import ChatThreadPage from './pages/ChatThreadPage'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import PoolAdminRoute from './components/PoolAdminRoute'
import MemberPoolDetailPage from './pages/MemberPoolDetailPage'
import LoadingSpinner from './components/LoadingSpinner'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
        <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: 'white',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: 'white',
            },
          },
        }}
      />
      </AuthProvider>
    </ThemeProvider>
  )
}

// Component to handle root redirect based on auth status
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth0()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  // Redirect based on authentication status
  return <Navigate to={isAuthenticated ? "/dashboard" : "/pending"} replace />
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/pending" element={<HomePage />} />
      <Route path="/login" element={<AuthLoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Layout><EventsPage /></Layout></ProtectedRoute>} />
      <Route path="/events/:eventId" element={<ProtectedRoute><Layout><EventDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/pools/*" element={<ProtectedRoute><Layout><PoolsPage /></Layout></ProtectedRoute>} />
      <Route path="/bourbon" element={<ProtectedRoute><Layout><BourbonSwapPage /></Layout></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><Layout><MembersPage /></Layout></ProtectedRoute>} />
      <Route path="/members/:userId" element={<ProtectedRoute><Layout><MemberDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Layout><ChatPage /></Layout></ProtectedRoute>} />
      <Route path="/chat/:kind/:entityId" element={<ProtectedRoute><Layout><ChatThreadPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy" element={<ProtectedRoute><Layout><LegacyPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/roster" element={<ProtectedRoute><Layout><HistoricRosterPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/kazy-cup" element={<ProtectedRoute><Layout><KazyCupPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/memorial" element={<ProtectedRoute><Layout><MemorialPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/gallery" element={<ProtectedRoute><Layout><GalleryPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/railbird-games" element={<ProtectedRoute><Layout><RailbirdGamesPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/railbird-games/create" element={<ProtectedRoute requireAdmin><Layout><CreateGamePage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/railbird-games/:gameId" element={<ProtectedRoute><Layout><GameDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/railbird-games/:gameId/edit" element={<ProtectedRoute requireAdmin><Layout><EditGamePage /></Layout></ProtectedRoute>} />
      <Route path="/legacy/ledger" element={<ProtectedRoute><Layout><RailbirdLedgerPage /></Layout></ProtectedRoute>} />
      <Route path="/locations" element={<ProtectedRoute requireAdmin><Layout><LocationsPage /></Layout></ProtectedRoute>} />
      
      {/* Golf Routes */}
      <Route path="/golf/outings" element={<ProtectedRoute><Layout><GolfOutingsPage /></Layout></ProtectedRoute>} />
      <Route path="/golf/courses" element={<ProtectedRoute requireAdmin><Layout><GolfCoursesPage /></Layout></ProtectedRoute>} />
      <Route path="/golf/outings/:outingId" element={<ProtectedRoute><Layout><GolfOutingDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/golf/outings/:outingId/scorecard" element={<ProtectedRoute><GolfScorecardPage /></ProtectedRoute>} />
      <Route path="/golf/outings/:outingId/leaderboard" element={<ProtectedRoute><GolfLeaderboardPage /></ProtectedRoute>} />
      
      <Route path="/admin" element={<ProtectedRoute requireAdmin><Layout><AdminPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/pools/football" element={<PoolAdminRoute><Layout><FootballPoolsAdminPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/football/create" element={<PoolAdminRoute><Layout><CreateFootballPoolPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/football/:poolId" element={<PoolAdminRoute><Layout><PoolDetailPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing" element={<PoolAdminRoute><Layout><HorseRacingPoolsAdminPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing/create" element={<PoolAdminRoute><Layout><CreateHorseRacingPoolPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing/:poolId" element={<PoolAdminRoute><Layout><HorseRacingPoolDetailPage /></Layout></PoolAdminRoute>} />
      <Route path="/pools/:poolId" element={<ProtectedRoute><Layout><PoolDetailRouter /></Layout></ProtectedRoute>} />
      <Route path="/pools/:poolId/picks" element={<ProtectedRoute><Layout><PoolPicksPage /></Layout></ProtectedRoute>} />
      <Route path="/pools/:poolId/my-picks" element={<ProtectedRoute><Layout><ViewMyPicksRouter /></Layout></ProtectedRoute>} />
      <Route path="/pools/:poolId/standings" element={<ProtectedRoute><Layout><PoolStandingsRouter /></Layout></ProtectedRoute>} />
      <Route path="/admin/pools/football/:poolId/results" element={<PoolAdminRoute><Layout><PoolGameResultsPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/football/:poolId/edit" element={<PoolAdminRoute><Layout><EditPoolPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/football/:poolId/dashboard" element={<PoolAdminRoute><Layout><div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><div className="text-center"><h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Pool Dashboard</h1><div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8"><div className="text-4xl mb-4">📊</div><p className="text-gray-600 dark:text-gray-300">Pool dashboard coming soon!</p><p className="text-sm text-gray-500 dark:text-gray-400 mt-2">This page will show pool statistics, leaderboards, and management tools.</p></div></div></div></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing/:poolId/picks" element={<PoolAdminRoute><Layout><HorseRacingPoolPicksPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing/:poolId/standings" element={<PoolAdminRoute><Layout><HorseRacingPoolStandingsPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing/:poolId/edit" element={<PoolAdminRoute><Layout><EditHorseRacingPoolPage /></Layout></PoolAdminRoute>} />
      <Route path="/admin/pools/horse-racing/:poolId/results" element={<PoolAdminRoute><Layout><HorseRacingPoolResultsPage /></Layout></PoolAdminRoute>} />
      <Route path="/pending-approval" element={<ProtectedRoute><Layout><PendingApprovalPage /></Layout></ProtectedRoute>} />
      
      {/* Catch all - redirect to root which will handle auth redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
